import { View, ScrollView } from "react-native";
import { useNavigation, CommonActions } from "@react-navigation/native";
import { History, Plus } from "lucide-react-native";
import {
  Dialog,
  DialogHeader,
  EmptyState,
  DialogIcon,
  CompactActionButton,
  Row,
  StatusBadge,
  type StatusBadgeTone,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import pastCycleIllustration from "../../../../assets/images/past-cycle-dialog-illustration.png";
import {
  usePastCyclesList,
  OUTCOME_COLORS,
  OUTCOME_ICONS,
  OUTCOME_LABEL_KEYS,
  type CycleOutcome,
} from "../../../hooks/usePastCyclesList";

// The same outcome tones the full past-cycles list uses.
const OUTCOME_TONES: Record<CycleOutcome, StatusBadgeTone> = {
  positive: "green",
  completed: "green",
  cancelled: "coral",
  ongoing: "gold",
  frozen: "blue",
  negative: "neutral",
};

type Props = {
  visible: boolean;
  onClose: () => void;
};

/**
 * Past cycles list shown as an in-place dialog (consistent with the other
 * profile modals). Each row opens that cycle's full recap.
 */
export default function PastCyclesModal({ visible, onClose }: Props) {
  const t = useTranslations();
  const navigation = useNavigation();
  const pastCycles = usePastCyclesList();

  const openCycleRecap = (cycleId: string) => {
    onClose();
    navigation.dispatch(
      CommonActions.navigate("Home", {
        screen: "Care",
        params: { screen: "PastCycleDetail", params: { cycleId } },
      }),
    );
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
    >
      <DialogHeader
        leading={
          pastCycles.length > 0 ? (
            <History size={22} color={EMBER.colors.textMid} strokeWidth={1.6} />
          ) : undefined
        }
        title={t("care.all_past_cycles.title")}
        style={{ marginBottom: 12 }}
      />

      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 420 }}
        contentContainerStyle={{ paddingBottom: 4 }}
      >
        {pastCycles.length === 0 ? (
          <EmptyState
            density="compact"
            titleVariant="featured"
            illustration={
              <DialogIcon
                source={pastCycleIllustration}
                width={68}
                height={80}
              />
            }
            title={t("care.all_past_cycles.no_past_cycles_yet")}
            subtitle={t(
              "care.all_past_cycles.add_previous_cycles_to_your_history",
            )}
            action={
              <CompactActionButton
                label={t("care.all_past_cycles.add_a_past_cycle")}
                action="care.all_past_cycles.add_a_past_cycle"
                variant="primary"
                leadingIcon={Plus}
                onPress={() => {
                  onClose();
                  navigation.dispatch(
                    CommonActions.navigate("Home", {
                      screen: "Care",
                      params: {
                        screen: "AllPastCycles",
                        params: { openAddDialog: true },
                      },
                    }),
                  );
                }}
              />
            }
          />
        ) : (
          <View style={{ gap: 8 }}>
            {pastCycles.map((cycle) => {
              const outcomeColor = OUTCOME_COLORS[cycle.outcome];
              const OutcomeIcon = OUTCOME_ICONS[cycle.outcome];
              const outcomeLabel = t(OUTCOME_LABEL_KEYS[cycle.outcome]);

              return (
                <Row
                  key={cycle.id}
                  // Inside the dialog's scroll area: the flat bordered fill
                  // rather than a glass row whose shadow would be clipped.
                  surface="input"
                  leading={
                    <OutcomeIcon
                      size={18}
                      color={outcomeColor}
                      strokeWidth={1.5}
                    />
                  }
                  leadingSize={18}
                  title={`${cycle.cycleType} #${cycle.cycleNumber}`}
                  titleRight={
                    <StatusBadge
                      label={outcomeLabel}
                      tone={OUTCOME_TONES[cycle.outcome]}
                    />
                  }
                  subtitle={`${cycle.startDate}${
                    cycle.endDate ? ` – ${cycle.endDate}` : ""
                  }`}
                  onPress={() => openCycleRecap(cycle.id)}
                  action="past_cycle_row"
                  paddingHorizontal={14}
                  paddingVertical={14}
                />
              );
            })}
          </View>
        )}
      </ScrollView>
    </Dialog>
  );
}
